import type { ButtonVariants } from '~/components/ui/button'
import type { VNode } from 'vue'

interface ConfirmationDialogState {
  open: boolean
  title: string
  description: string | VNode
  confirmText: string
  cancelText: string
  submitText: string
  buttonExtras?: VNode
  variant: ButtonVariants['variant']
  cancelVariant: ButtonVariants['variant']
  resolver: (r: boolean) => void
  requireConfirmation: boolean
}

export function useConfirmation() {
  const state = useState<ConfirmationDialogState>('ConfirmationState', () => ({
    open: false,
    requireConfirmation: true,
    title: '',
    description: '',
    confirmText: '',
    cancelText: '',
    submitText: '',
    buttonExtras: undefined,
    variant: 'destructive',
    cancelVariant: 'secondary',
    resolver: () => {},
  }))

  async function confirmation({
    title = 'Confirmation',
    description = 'Are you sure?',
    confirmText = 'I understand and wish to continue',
    cancelText = 'Cancel',
    submitText = 'Confirm',
    variant = 'destructive',
    cancelVariant = 'secondary',
    buttonExtras,
    requireConfirmation = true,
  }: Partial<ConfirmationDialogState> = {}): Promise<boolean> {
    if (import.meta.server) return false // Don't let this run on the server and hang SSR

    state.value = {
      ...state.value,
      open: true,
      requireConfirmation,
      title,
      description,
      confirmText,
      cancelText,
      cancelVariant,
      submitText,
      variant,
      buttonExtras,
    }
    const result = await new Promise<boolean>(resolve => {
      state.value.resolver = resolve
    })
    state.value.resolver = () => {}
    state.value.open = false
    return result
  }

  function submit(value: boolean): void {
    state.value.resolver(value)
  }

  return {
    confirmation,
    submit,
    state,
  }
}
